import React, { useEffect, useState } from 'react';
import { LuLayoutGrid } from 'react-icons/lu';
import { api, getApiData } from '../api';
import { defaultWebsiteSections, normalizeWebsiteSections, websiteSectionOptions } from '../../config/websiteSections';
import { showError, showUpdateSuccess } from '../utils/adminAlerts';

const AdminWebsiteSections = () => {
  const [sections, setSections] = useState(defaultWebsiteSections);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadSections = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.getWebsiteSections();
        if (isMounted) {
          setSections(normalizeWebsiteSections(getApiData(response)));
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Could not load website sections.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSections();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggle = (key) => {
    setSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const response = await api.updateWebsiteSections(sections);
      setSections(normalizeWebsiteSections(getApiData(response)));
      showUpdateSuccess('Website sections updated successfully.');
    } catch (err) {
      const message = err.message || 'Could not save website sections.';
      setError(message);
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 text-sm text-slate-500 dark:border-white/10 dark:bg-[#101010] dark:text-gray-400">
        Loading website sections...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#101010]">
        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
            <LuLayoutGrid size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Website Sections</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-gray-400">
              Enable or disable homepage sections. Disabled sections will be hidden from the public website and related
              navigation links.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {websiteSectionOptions.map((option) => {
              const enabled = sections[option.key] !== false;

              return (
                <div
                  key={option.key}
                  className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-4 transition-colors ${
                    enabled
                      ? 'border-primary/30 bg-primary/5 dark:border-primary/20 dark:bg-primary/10'
                      : 'border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-[#151515]'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{option.label}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-gray-400">{option.hint}</p>
                  </div>

                  <div className="flex w-[7.5rem] shrink-0 items-center justify-end gap-3">
                    <span
                      className={`text-[11px] font-semibold uppercase tracking-wide ${
                        enabled ? 'text-primary' : 'text-slate-400 dark:text-gray-500'
                      }`}
                    >
                      {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      aria-label={`${option.label} ${enabled ? 'enabled' : 'disabled'}`}
                      onClick={() => handleToggle(option.key)}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                        enabled ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform duration-200 ${
                          enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {error ? <p className="text-sm text-red-500">{error}</p> : null}

          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-primary px-6 py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02] disabled:opacity-70"
          >
            {saving ? 'Saving...' : 'Save Section Settings'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminWebsiteSections;
